import React, { useState } from "react";
import {
  Box,
  Tab,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Alert,
  useTheme,
} from "@mui/material";

import SecurityIcon from "@mui/icons-material/Security";
import CloseIcon from "@mui/icons-material/Close";

import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";

import { useQuery } from "@apollo/client";
import {
  GET_RISK_BY_ID,
  GET_CONTROLS_WITH_CHECKS_BY_RISK_ID,
  GET_CONTROLS_WITH_CHECKS_BY_RISK_ASSESSMENT_ID,
  GET_RISK_ASSESSMENT_ITEMS_BY_ID,
} from "../../../../../../apis/organization-apis/risk-apis/riskApis";
import RegisterOverviewTab from "./RiskDetailsTabs/RegisterOverviewTab";
import AssessmentOverviewTab from "./RiskDetailsTabs/AssessmentOverviewTab";
import MappedControlContent from "./RiskDetailsTabs/ControlsMappingTab";
import DialogSkeleton from "../../../../../../components/Skeletons/DialogSkeleton";
import AccordionSkeleton from "../../../../../../components/Skeletons/AccordionSkeleton";
import { TabName } from "../../../utils/enums";

export default function RiskDetailDialog({
  open,
  onClose,
  risk,
  type,
  refetchItems,
  isEditable,
}) {
  const [tab, setTab] = useState("0");
  const theme = useTheme();

  const {
    data,
    loading: riskLoading,
    error: riskError,
    refetch: refetchRiskById,
  } = useQuery(GET_RISK_BY_ID, {
    variables: { id: risk?.id },
    skip: !risk?.id || type !== TabName.REGISTER,
    fetchPolicy: "network-only",
  });

  const { data: controlsDataQuery, loading: controlsLoading } = useQuery(
    GET_CONTROLS_WITH_CHECKS_BY_RISK_ID,
    {
      variables: { risk_id: risk?.id },
      skip: !risk?.id || type !== TabName.REGISTER,
      fetchPolicy: "network-only",
    }
  );

  const {
    data: assessmentItemData,
    loading: assessmentItemLoading,
    error: assessmentItemError,
    refetch: refetchAssessmentItem,
  } = useQuery(GET_RISK_ASSESSMENT_ITEMS_BY_ID, {
    variables: { assessment_id: risk?.assessment_id },
    skip:
      !risk?.original_risk_id ||
      !risk?.assessment_id ||
      type !== TabName.ASSESSMENT,
    fetchPolicy: "network-only",
  });

  const { data: liveRegisterRiskData, refetch: refetchLiveRisk } = useQuery(
    GET_RISK_BY_ID,
    {
      variables: { id: risk?.original_risk_id },
      skip: !risk?.original_risk_id || type !== TabName.ASSESSMENT,
      fetchPolicy: "network-only",
    }
  );

  const {
    data: controlsDataQueryAssessment,
    loading: controlsLoadingAssessment,
  } = useQuery(GET_CONTROLS_WITH_CHECKS_BY_RISK_ASSESSMENT_ID, {
    variables: {
      risk_id: risk?.original_risk_id,
      assessment_id: risk?.assessment_id,
    },
    skip:
      !risk?.original_risk_id ||
      !risk?.assessment_id ||
      type !== TabName.ASSESSMENT,
    fetchPolicy: "network-only",
  });

  const liveRegisterRisk = liveRegisterRiskData?.getRiskById?.data;
  const assessmentItem =
    assessmentItemData?.getRiskAssessmentItemsById?.data.find(
      (item) => item.original_risk_id === risk?.original_risk_id
    );

  const fetchedRisk =
    type === TabName.REGISTER
      ? data?.getRiskById?.data
      : assessmentItem
        ? {
            ...assessmentItem,
            risk_owner_email:
              liveRegisterRisk?.risk_owner_email ??
              assessmentItem?.risk_owner_email,
          }
        : assessmentItem;

  const controlsData =
    type === TabName.REGISTER
      ? controlsDataQuery?.getControlsWithChecksByRiskId?.data
      : controlsDataQueryAssessment?.getControlsWithChecksByRiskAssessmentId
        ?.data;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 3,
          maxHeight: "90vh",
          boxShadow: theme.customColors.shadow.main,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: "bold",
          fontSize: "16px",
          background: theme.customColors.background.cardHeader,
          borderBottom: `1px solid ${theme.customColors.border.main}`,
          color: theme.customColors.text.strong,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <SecurityIcon
            sx={{ color: theme.customColors.icons.dark, fontSize: 24 }}
          />
          <Box>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{
                lineHeight: 1.2,
                color: theme.customColors.text.strong,
              }}
            >
              {fetchedRisk?.key || risk?.risk_key} :{" "}
              {fetchedRisk?.title || risk?.risk_title || "Risk Details"}
            </Typography>
          </Box>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.customColors.text.secondary,
            "&:hover": {
              backgroundColor: theme.customColors.background.hover,
              color: theme.customColors.text.strong,
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, overflowY: "auto", minHeight: 200 }}>
        {riskLoading || assessmentItemLoading ? (
          <DialogSkeleton open={open} onClose={onClose} />
        ) : riskError || assessmentItemError ? (
          <Alert
            severity="error"
            sx={{
              backgroundColor: theme.customColors.status.error.bg,
              border: `1px solid ${theme.customColors.status.error.border}`,
              color: theme.customColors.status.error.text,
            }}
          >
            Failed to load risk details.
          </Alert>
        ) : !fetchedRisk ? (
          <Alert
            severity="info"
            sx={{
              backgroundColor: theme.customColors.background.info,
              border: `1px solid ${theme.customColors.border.accent}`,
              color: theme.customColors.text.secondary,
            }}
          >
            No risk details found.
          </Alert>
        ) : (
          <TabContext value={tab}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: theme.customColors.border.subtle,
                background: theme.customColors.background.header,
                px: 3,
                pt: 2,
              }}
            >
              <TabList
                onChange={(_, v) => setTab(v)}
                aria-label="Risk Detail Tabs"
              >
                <Tab
                  label="Overview"
                  value="0"
                  sx={{
                    fontWeight: 600,
                    textTransform: "none",
                    fontSize: "14px",
                    color: theme.customColors.text.secondary,
                    "&.Mui-selected": {
                      color: theme.customColors.text.strong,
                    },
                  }}
                />
                <Tab
                  label="Controls & Checks"
                  value="1"
                  sx={{
                    fontWeight: 600,
                    textTransform: "none",
                    fontSize: "14px",
                    color: theme.customColors.text.secondary,
                    "&.Mui-selected": {
                      color: theme.customColors.text.strong,
                    },
                  }}
                />
              </TabList>
            </Box>
            <TabPanel value="0" sx={{ p: 3, pb: 1, height: "auto" }}>
              {type === TabName.REGISTER ? (
                <RegisterOverviewTab
                  risk={{
                    ...fetchedRisk,
                    controls: controlsData,
                  }}
                  refetchItems={() => {
                    refetchRiskById();
                    refetchItems();
                  }}
                  onClose={onClose}
                />
              ) : (
                <AssessmentOverviewTab
                  risk={fetchedRisk}
                  isEditable={isEditable}
                  refetchItems={() => refetchAssessmentItem()}
                  refetchLiveRisk={refetchLiveRisk}
                  onClose={onClose}
                />
              )}
            </TabPanel>
            <TabPanel value="1" sx={{ p: 2, height: "auto" }}>
              {(
                type === TabName.REGISTER
                  ? controlsLoading
                  : controlsLoadingAssessment
              ) ? (
                <AccordionSkeleton />
              ) : (
                <MappedControlContent
                  data={controlsData}
                  loading={
                    type === TabName.REGISTER
                      ? controlsLoading
                      : controlsLoadingAssessment
                  }
                />
              )}
            </TabPanel>
          </TabContext>
        )}
      </DialogContent>
    </Dialog>
  );
}
